/**
 * kemet-globo-gallery.js
 *
 * Instead of trying to read Globo's internal data (which isn't exposed globally),
 * this script MIRRORS whatever Globo does to the standard slideshow carousel
 * into our custom two-column kemet-gallery-layout grid.
 *
 * Strategy:
 * 1. Watch the slideshow for any attribute/visibility changes using MutationObserver.
 * 2. After any change, read which slides are visible vs hidden.
 * 3. Match each slide to a grid item by comparing the image src URL.
 * 4. Show/hide the grid items to match the slideshow's state.
 */
(function () {

  // Normalize an image URL to a comparable base (strip query params and CDN size params)
  function normalizeUrl(url) {
    if (!url) return '';
    try {
      // Strip query string
      url = url.split('?')[0];
      // Strip Shopify CDN size suffixes like _600x, _1200x800, etc.
      url = url.replace(/_\d+x\d*\.(jpg|jpeg|png|webp|gif)/i, '.$1');
      url = url.replace(/_\d+x\d*$/, '');
      // Strip protocol for comparison
      url = url.replace(/^https?:/, '');
      return url.toLowerCase();
    } catch (e) {
      return url;
    }
  }

  function getMediaId(el) {
    const mediaEl = el.hasAttribute('data-media-id') ? el : el.querySelector('[data-media-id]');
    return mediaEl ? mediaEl.getAttribute('data-media-id') : null;
  }

  // Check if an element is hidden by Globo
  function isHiddenByGlobo(el) {
    var refAttr = el.getAttribute('ref');
    return el.hasAttribute('hidden') ||
           el.style.display === 'none' ||
           el.classList.contains('globo-sw-media--hide') ||
           (refAttr !== null && refAttr.indexOf('ref-hidden') !== -1);
  }

  function syncGridWithSlideshow() {
    const mediaGallery = document.querySelector('media-gallery');
    if (!mediaGallery) return;

    // Get all slideshow slides and their visibility
    const slides = Array.from(mediaGallery.querySelectorAll('slideshow-slide'));
    if (slides.length === 0) return;

    // Build a map: mediaId -> isVisible
    const visibilityMap = new Map();
    let anyHidden = false;

    slides.forEach(slide => {
      const mediaId = getMediaId(slide);
      const mediaItem = slide.querySelector('.product__media-item') || slide;
      const mediaDiv = slide.querySelector('.product-media');
      
      const hidden = isHiddenByGlobo(slide) || 
                     isHiddenByGlobo(mediaItem) || 
                     (mediaDiv && isHiddenByGlobo(mediaDiv));
                     
      if (hidden) anyHidden = true;
      if (mediaId) {
        if (!visibilityMap.has(mediaId) || visibilityMap.get(mediaId) === false) {
          visibilityMap.set(mediaId, !hidden);
        }
      }
    });

    // Now sync the custom grid
    const stickyEl = mediaGallery.querySelector('.kemet-gallery-sticky');
    const scrollEl = mediaGallery.querySelector('.kemet-gallery-scroll');
    if (!stickyEl && !scrollEl) return;

    // Collect all grid items
    const gridItems = [];
    if (stickyEl) {
      Array.from(stickyEl.children).forEach(child => {
        if (child.classList.contains('product-media-container') || child.hasAttribute('data-media-id')) {
          gridItems.push(child);
        }
      });
    }
    if (scrollEl) {
      Array.from(scrollEl.querySelectorAll(':scope > li, :scope > div')).forEach(child => {
        gridItems.push(child);
      });
    }

    if (gridItems.length === 0) return;

    // Check if Globo has filtered by removing elements completely
    const globoRemovedElements = slides.length < gridItems.length;

    // If nothing is hidden and nothing was removed, Globo hasn't filtered yet — skip
    if (!anyHidden && !globoRemovedElements) return;

    // Determine visibility for each grid item
    const matchedVisible = [];
    const matchedHidden = [];

    gridItems.forEach(item => {
      const mediaId = getMediaId(item);
      if (!mediaId) {
        matchedVisible.push(item);
        return;
      }

      // If it's missing from visibilityMap (meaning Globo removed it from slideshow), it's hidden.
      // Otherwise, use the visibility state from the slideshow.
      let visible = visibilityMap.has(mediaId) ? visibilityMap.get(mediaId) : false;

      if (visible) {
        matchedVisible.push(item);
      } else {
        matchedHidden.push(item);
      }
    });

    // Safety: if everything would be hidden, don't hide anything
    if (matchedVisible.length === 0) return;

    // Apply visibility
    matchedHidden.forEach(item => {
      item.style.setProperty('display', 'none', 'important');
      item.setAttribute('hidden', '');
    });

    matchedVisible.forEach(item => {
      item.style.removeProperty('display');
      item.removeAttribute('hidden');
    });

    // Move first visible item to sticky if needed
    if (stickyEl && matchedVisible.length > 0 && matchedVisible[0].parentElement !== stickyEl) {
      stickyEl.appendChild(matchedVisible[0]);
    }

    // Move remaining visible items to scroll
    if (scrollEl && matchedVisible.length > 1) {
      for (let i = 1; i < matchedVisible.length; i++) {
        if (matchedVisible[i].parentElement !== scrollEl) {
          scrollEl.appendChild(matchedVisible[i]);
        }
      }
    }
  }

  function initialize() {
    const mediaGallery = document.querySelector('media-gallery');
    if (!mediaGallery) return;

    // Watch for any attribute mutations on the entire media-gallery subtree
    // Globo changes 'hidden', 'style', or 'ref' attributes when filtering
    const observer = new MutationObserver(function(mutations) {
      let relevant = false;
      for (const mutation of mutations) {
        // React to attribute changes on product__media-item or slideshow-slide elements
        if (mutation.target.classList && mutation.target.classList.contains('product__media-item')) {
          relevant = true;
          break;
        }
        if (mutation.target.closest && (mutation.target.closest('.product__media-item') || mutation.target.closest('slideshow-slide'))) {
          relevant = true;
          break;
        }
      }
      if (relevant) {
        // Debounce slightly to let all mutations settle
        clearTimeout(window._kemetSyncTimer);
        window._kemetSyncTimer = setTimeout(syncGridWithSlideshow, 50);
      }
    });

    observer.observe(mediaGallery, {
      attributes: true,
      attributeFilter: ['hidden', 'style', 'ref'],
      subtree: true,
      childList: true
    });

    // Also run on variant:update in case MutationObserver misses something
    document.addEventListener('variant:update', function () {
      setTimeout(syncGridWithSlideshow, 300);
    });

    // Initial sync after a small delay to let Globo run first
    setTimeout(syncGridWithSlideshow, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);

})();